import React from "react";
import { observer } from "mobx-react-lite";
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { CancelIcon } from "../../icons";
import { useRootStore } from "../../store/contexts";

export const ActivityQueuePanel = observer(() => {
  const rootStore = useRootStore();
  const { activityStore, activityQueueStore, langStore } = rootStore;

  const handleClose = () => {
    activityQueueStore.closePanel();
  };

  const handleCancel = (id: string) => {
    activityStore.cancelOperation(id);
  };

  const formatElapsed = (startedAt: number): string => {
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    if (elapsed < 60) return `${elapsed}s`;
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <Popover
      open={activityQueueStore.panelOpen}
      anchorEl={activityQueueStore.anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <Box sx={{ width: 400, maxHeight: 500 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {langStore.activeOperationsLabel}
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          {activityStore.activeOperations.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {langStore.noActiveOperations}
              </Typography>
            </Box>
          ) : (
            <List dense>
              {activityStore.activeOperations.map((op) => (
                <ListItem
                  key={op.id}
                  secondaryAction={
                    op.policy.manualCancellable && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleCancel(op.id)}
                        title={langStore.cancelOperationLabel}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    primary={op.detail ? `${op.operation}: ${op.detail}` : op.operation}
                    secondary={`${op.type} • ${formatElapsed(op.startedAt)}`}
                    primaryTypographyProps={{
                      variant: "body2",
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Popover>
  );
});
